import React, { useState, useEffect, useRef } from 'react'
import { useSwipeable } from 'react-swipeable'
import { Container, Image, TextBody, Icon } from '@pdrj/pmi-iqos-ds-react'
import { Assets } from '@pdrj/pmi-iqos-shared-react'

const TRACK_W = 250
const PAD = 4
const PILL_W = 76
const MAX_X = TRACK_W - PAD * 2 - PILL_W // 166

const colors = {
  white: '#FFFDFB',
  black: '#000000',
  blackBackgroundOverlay: '#00000080',
  iqosBorderColor: '#00D1D2',
  iqosGradient1: '#FFD040',
  iqosGradient2: '#EAB407'
}
const clamp = (n, min, max) => Math.max(min, Math.min(max, n))

const SlideButton = ({
  text = 'Swipe',
  textUnlocked = '10 Points Used',
  pillLabel = '-10',
  showPillIcon = true,
  disabled = false,
  onSuccess,
  onFailure,
  iconUrl = Assets.urls.iconPoints,
  'data-testing': dataTesting
}) => {
  const [dragX, setDragX] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [hideArrow, setHideArrow] = useState(false)

  const prevDisabledRef = useRef(disabled)

  useEffect(() => {
    const wasDisabled = prevDisabledRef.current
    prevDisabledRef.current = disabled

    // disabled: true -> false
    if (wasDisabled && !disabled) {
      setDragX(0)
      setIsDragging(false)
      setIsSuccess(false)
      setHideArrow(false)
    }
  }, [disabled])

  const handlers = useSwipeable({
    trackMouse: true,
    preventScrollOnSwipe: true,
    onSwipeStart: () => {
      if (disabled || isSuccess) return
      setIsDragging(true)
    },
    onSwiping: e => {
      if (disabled || isSuccess) return
      setDragX(clamp(e.deltaX, 0, MAX_X))
    },
    onSwiped: () => {
      if (disabled || isSuccess) return
      setIsDragging(false)

      if (dragX >= MAX_X * 0.9) {
        setIsSuccess(true)
        onSuccess?.()
        setHideArrow(true)
      } else {
        setDragX(0)
        onFailure?.()
      }
    }
  })

  return (
    <div
      // need to pass handlers to a div
      {...handlers}
      data-testing={dataTesting}
      style={{
        width: `${TRACK_W}px`,
        userSelect: 'none',
        pointerEvents: disabled ? 'none' : 'auto',
        opacity: disabled ? 0.6 : 1,
        backgroundColor: colors.blackBackgroundOverlay,
        borderRadius: '9999px',
        border: `1px solid ${
          showPillIcon ? colors.iqosBorderColor : colors.white
        }`
      }}>
      {/* TRACK */}
      <Container
        display={Container.displays.flex}
        flexShrink={0}
        justifyContent={Container.justifyContentValues.start}
        alignItems={Container.alignItemsValues.center}
        position={Container.positions.relative}
        overflowHidden
        p="1"
        width="62"
        height="13"
        additionalClasses="q-flex-grow-0 q-flex-shrink-0">
        {/* CENTER LABEL */}
        <Container
          position={Container.positions.absolute}
          t="0"
          r="0"
          b="0"
          l="0"
          display={Container.displays.flex}
          alignItems={Container.alignItemsValues.center}
          justifyContent={Container.justifyContentValues.center}
          additionalClasses="q-pointer-events-none">
          <Container
            justifyContent={Container.justifyContentValues.center}
            display={Container.displays.flex}
            pl="5">
            <TextBody
              darkMode
              size={TextBody.sizes.m}
              additionalClasses="q-font-bold">
              {isSuccess ? textUnlocked : text}
            </TextBody>
          </Container>
          {!hideArrow ? (
            <Container position={Container.positions.absolute} r="0" pr="5">
              <TextBody darkMode>
                <Icon size={Icon.sizes.s} image={Icon.images.arrowRight} />
              </TextBody>
            </Container>
          ) : null}
        </Container>

        {/* PILL needs real DOM for transform */}
        <div
          style={{
            width: '76px',
            height: '40px',
            transform: `translateX(${dragX}px)`,
            transition: isDragging
              ? 'none'
              : isSuccess
              ? 'opacity 220ms ease'
              : 'transform 220ms ease',
            opacity: isSuccess ? 0 : 1,
            borderRadius: '999px',
            background: `radial-gradient(50% 50% at 50% 50%, ${colors.iqosGradient1} 57.69%, ${colors.iqosGradient2} 100%)`,
            border: `1px solid ${colors.black}`,
            boxSizing: 'border-box'
          }}>
          <Container
            display={Container.displays.flex}
            alignItems={Container.alignItemsValues.center}
            justifyContent={Container.justifyContentValues.center}
            flexShrink={0}
            additionalClasses="q-w-full q-h-full q-flex-grow-0 q-flex-shrink-0">
            <Container
              display={Container.displays.flex}
              alignItems={Container.alignItemsValues.center}
              flexShrink={0}
              justifyContent={Container.justifyContentValues.center}
              additionalClasses="q-gap-1 q-pointer-events-none">
              <TextBody
                size={TextBody.sizes.m}
                additionalClasses="q-font-bold q-leading-none q-text-black">
                {pillLabel}
              </TextBody>
              {showPillIcon && (
                <Image src={iconUrl} alt="" width="6" height="6" contain />
              )}
            </Container>
          </Container>
        </div>
      </Container>
    </div>
  )
}

export default SlideButton
